import {
  ReferrablBoxPrizeableType,
  type ReferralBoxPrizeableCredit,
  type ReferralBoxPrizeablePlan,
} from '@/services/referral-boxes'
import type {
  Benefit,
  Campaign,
  Deposit,
  InstantWinDraw,
  InstantWinMainPrize,
  InstantWinPrize,
  PartnershipCampaign,
  Plan,
  Prize,
  PrizeCar,
  Product,
  User,
  Winner,
} from '@/types'

/**
 * Checks if the value is a valid User object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isUser = (value: any): value is User =>
  !!value?.__type && value.__type === 'User' && !!value.id

/**
 * Checks if the value is a valid Plan object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isPlan = (value: any): value is Plan =>
  !!value?.__type && value.__type === 'Plan'

/**
 * Checks if the value is a valid Prize object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isPrize = (value: any): value is Prize =>
  (!!value?.__type && value.__type === 'Prize') ||
  (!!value?.__type && value.__type === 'PrizeCar')

/**
 *Checks if the value is a valid PrizeCar object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isPrizeCar = (value: any): value is PrizeCar =>
  !!value?.__type && value.__type === 'PrizeCar'

/**
 * Checks if the value is a valid Winner object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isWinner = (value: any): value is Winner =>
  !!value?.__type && value.__type === 'Winner'

/**
 * Checks if the value is a valid InstantWinMainPrize object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isInstantWinMainPrize = (
  value: any
): value is InstantWinMainPrize =>
  !!value?.__type && value.__type === 'InstantWinMainPrize'

/**
 * Checks if the value is a valid InstantWinDraw object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isInstantWinDraw = (value: any): value is InstantWinDraw =>
  !!value?.__type && value.__type === 'InstantWinDraw'

/**
 * Checks if the value is a valid InstantWinPrize object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isInstantWinPrize = (value: any): value is InstantWinPrize =>
  !!value?.__type && value.__type === 'InstantWinPrize'

/**
 * Checks if the value is a valid Campaign object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isCampaign = (value: any): value is Campaign =>
  !!value?.__type && value.__type === 'Campaign'

/**
 * Checks if the value is a valid PartnershipCampaign object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isPartnershipCampaign = (
  value: any
): value is PartnershipCampaign =>
  !!value?.__type && value.__type === 'PartnershipCampaign'

/**
 * Checks if the value is a valid Benefit object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isBenefit = (value: any): value is Benefit =>
  !!value?.__type && value.__type === 'Benefit'

/**
 * Checks if the value is a valid ReferralBoxPrize credit object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isReferralBoxPrizeCredit = (
  value: any
): value is ReferralBoxPrizeableCredit =>
  !!value?.referral_box_prizeable_type_id &&
  value.referral_box_prizeable_type_id === ReferrablBoxPrizeableType.Credit

/**
 * Checks if the value is a valid ReferralBoxPrize plan object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isReferralBoxPrizePlan = (
  value: any
): value is ReferralBoxPrizeablePlan =>
  !!value?.referral_box_prizeable_type_id &&
  value.referral_box_prizeable_type_id === ReferrablBoxPrizeableType.Plan

/**
 * Checks if the value is a valid Deposit object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isDeposit = (value: any): value is Deposit =>
  !!value?.__type && value.__type === 'Deposit'

/**
 * Checks if the value is a valid purchasable product.
 * @param {any} value
 * @returns {boolean}
 */
export const isPurchasableProduct = (value: any): value is Product =>
  isPlan(value) || isInstantWinDraw(value) || isDeposit(value)
